import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import styled from "@emotion/styled";
import { ActivityIcon, BugIcon, InfoIcon, WifiIcon } from "lucide-react";
import React, { useState } from "react";
import ReactDOM from "react-dom/client";
import { CHAT_MODE, useChatState } from "@/store/chat-state";
import { useModalState } from "@/store/modal-state";

import { Sentry } from "../../instrument";
import { colors, radius, spacing } from "../design-tokens";
import { env } from "@/env";
import { injectBaseStyles } from "@/utils/widget-runtime";

const API_URL = env.API_URL;

type DebugDashboardProps = {
  showInfo?: boolean;
};

const Container = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  padding: ${spacing.md}px;
  border: 1px dashed #6b7280;
  border-radius: ${radius.md}px;
  background: rgba(107, 114, 128, 0.05);
  max-width: 800px;
`;

const TestGrid = styled.div`
  display: flex;
  gap: ${spacing.md}px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const TestSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  padding: ${spacing.sm}px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.sm}px;
  background: rgba(255, 255, 255, 0.7);
  flex: 1;
  min-width: 200px;
`;

const TestButton = styled.button<{ variant: "api" | "sentry" }>`
  min-width: 180px;
  border-radius: ${radius.sm}px;
  border: 1px solid
    ${(props) => (props.variant === "api" ? "#3b82f6" : "#ef4444")};
  background: ${(props) =>
    props.variant === "api"
      ? "rgba(59, 130, 246, 0.1)"
      : "rgba(239, 68, 68, 0.1)"};
  color: ${(props) => (props.variant === "api" ? "#3b82f6" : "#ef4444")};
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm}px;
  padding: ${spacing.sm}px ${spacing.md}px;
  cursor: pointer;
  outline: none;
  transition: all 200ms ease-in-out;

  &:hover {
    background: ${(props) =>
      props.variant === "api"
        ? "rgba(59, 130, 246, 0.2)"
        : "rgba(239, 68, 68, 0.2)"};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const TestIcon = styled.div<{ variant: "api" | "sentry" }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ResultWindow = styled.div<{ variant: "api" | "sentry" | "neutral" }>`
  font-size: 12px;
  line-height: 1.4;
  padding: ${spacing.sm}px;
  background: ${(props) =>
    props.variant === "api"
      ? "rgba(59, 130, 246, 0.05)"
      : props.variant === "sentry"
        ? "rgba(239, 68, 68, 0.05)"
        : "rgba(107, 114, 128, 0.05)"};
  border-radius: ${radius.sm}px;
  border: 1px solid ${colors.border};
  min-height: 40px;
  color: ${colors.text};
`;

const ContextUrlSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  padding: ${spacing.sm}px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.sm}px;
  background: rgba(255, 255, 255, 0.7);
`;

const ContextUrlControls = styled.div`
  display: flex;
  gap: ${spacing.sm}px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const ContextUrlInput = styled.input`
  width: 100%;
  padding: ${spacing.sm}px;
  border: 1px solid ${colors.border};
  border-radius: ${radius.sm}px;
  font-size: 14px;
  outline: none;
  background: white;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${colors.muted};
  }
`;

const ContextUrlButton = styled.button`
  padding: ${spacing.sm}px ${spacing.md}px;
  border-radius: ${radius.sm}px;
  border: 1px solid #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms ease-in-out;

  &:hover {
    background: rgba(59, 130, 246, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContextUrlLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text};
`;

const InfoSection = styled.div`
  font-size: 12px;
  color: ${colors.muted};
  line-height: 1.4;
  padding: ${spacing.sm}px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: ${radius.sm}px;
  border: 1px solid ${colors.border};
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs}px;
  font-weight: 600;
  margin-bottom: ${spacing.xs}px;
  color: ${colors.text};
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: ${spacing.md}px;

  li {
    margin-bottom: 2px;
  }
`;

const DebugDashboardComponent: React.FC<DebugDashboardProps> = ({
  showInfo = true,
}) => {
  const [apiResult, setApiResult] = useState<string>("");
  const [sentryResult, setSentryResult] = useState<string>("");
  const [apiTesting, setApiTesting] = useState(false);
  const [sentryCount, setSentryCount] = useState(0);
  const [inputContextUrl, setInputContextUrl] = useState<string>("");

  // Get store states
  const { openModal } = useModalState();
  const { setContextUrl, setUserQuery, setMode } = useChatState();

  // Get current state non-reactively to avoid render conflicts
  const getCurrentState = () => {
    const state = useChatState.getState();
    return {
      userQuery: state.userQuery,
      mode: state.mode,
      contextUrl: state.contextUrl,
    };
  };

  const handleApiTest = async () => {
    setApiTesting(true);
    setApiResult("⏳ Testing API connection...");

    try {
      const start = Date.now();
      const response = await fetch(`${API_URL}/health`);
      const latency = Date.now() - start;

      if (response.ok) {
        const data = await response.json();
        setApiResult(
          `✅ API Online - ${latency}ms\nStatus: ${data.status}\nURL: ${API_URL}`
        );
      } else {
        setApiResult(`❌ API Error - ${response.status}\nURL: ${API_URL}`);
      }
    } catch (error) {
      setApiResult(
        `❌ Connection Failed\nError: ${error instanceof Error ? error.message : "Unknown error"}\nURL: ${API_URL}`
      );
    } finally {
      setApiTesting(false);
    }
  };

  const handleSentryTest = () => {
    const errorNum = sentryCount + 1;
    const errorMessage = `🐛 Debug Test Error #${errorNum}`;

    try {
      throw new Error(errorMessage);
    } catch (error) {
      // Capture with Sentry
      Sentry.captureException(error, {
        tags: {
          component: "debug-dashboard",
          test: "manual-error-test",
          errorNumber: errorNum.toString(),
        },
        extra: {
          url: window.location.href,
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
          testCount: errorNum,
        },
        level: "error",
      });

      setSentryCount(errorNum);
      setSentryResult(
        `🐛 Error #${errorNum} sent to Sentry\nTime: ${new Date().toLocaleTimeString()}\nMode: ${import.meta.env.MODE}\nSentry: ${import.meta.env.VITE_SENTRY_DSN ? "Configured" : "Missing"}`
      );

      console.error("Error:", error);
      console.info(
        "Sentry DSN:",
        import.meta.env.VITE_SENTRY_DSN ? "Configured" : "Missing"
      );
      console.info("Environment:", import.meta.env.MODE);

      // Re-throw to potentially trigger ErrorBoundary
      throw error;
    }
  };

  const handleSetContextUrl = () => {
    if (inputContextUrl.trim()) {
      setContextUrl(inputContextUrl.trim());
    }
  };

  const handleClearContextUrl = () => {
    setContextUrl(undefined);
    setInputContextUrl("");
  };

  const handleSummarize = () => {
    if (inputContextUrl.trim()) {
      // Set the context URL
      setContextUrl(inputContextUrl.trim());
      
      // For summarization from debug dashboard, use a generic title
      // context-trigger uses an attribute to set the title
      setUserQuery("Page Summary");
      
      // Set mode to INPUT to trigger the chat flow
      setMode(CHAT_MODE.INPUT);
      
      // Open the modal to show the response
      openModal();
    }
  };

  return (
    <Container>
      <InfoHeader>
        <ActivityIcon size={14} />
        Debug Dashboard
      </InfoHeader>

      <TestGrid>
        <TestSection>
          <TestButton
            variant="api"
            onClick={handleApiTest}
            disabled={apiTesting}
          >
            <TestIcon variant="api">
              <WifiIcon size={16} />
            </TestIcon>
            Test API Health
          </TestButton>
          <ResultWindow variant="api">
            {apiResult ?? "Click to test API connection"}
          </ResultWindow>
        </TestSection>

        <TestSection>
          <TestButton variant="sentry" onClick={handleSentryTest}>
            <TestIcon variant="sentry">
              <BugIcon size={16} />
            </TestIcon>
            Test Sentry Error
          </TestButton>
          <ResultWindow variant="sentry">
            {sentryResult ?? "Click to test error monitoring"}
          </ResultWindow>
        </TestSection>
      </TestGrid>

      <ContextUrlSection>
        <ContextUrlLabel>Context URL Testing (Summarization)</ContextUrlLabel>
        <ContextUrlControls>
          <ContextUrlInput
            type="text"
            value={inputContextUrl}
            onChange={(e) => setInputContextUrl(e.target.value)}
            placeholder="Enter a URL to summarize (e.g., https://example.com/page)"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSummarize();
              }
            }}
          />
          <ContextUrlButton
            onClick={handleSummarize}
            disabled={!inputContextUrl.trim()}
            style={{ minWidth: "120px" }}
          >
            Summarize
          </ContextUrlButton>
          <ContextUrlButton
            onClick={handleClearContextUrl}
            disabled={!getCurrentState().contextUrl}
          >
            Clear
          </ContextUrlButton>
        </ContextUrlControls>
        <ResultWindow variant="neutral">
          {getCurrentState().contextUrl ? (
            <>
              <div>{`Context URL: ${getCurrentState().contextUrl}`}</div>
              <div>
                {`Status: ${getCurrentState().mode === CHAT_MODE.ANSWERING ? "🔄 Summarizing..." : "Ready"}`}
              </div>
            </>
          ) : (
            "No context URL set"
          )}
        </ResultWindow>
      </ContextUrlSection>

      {showInfo && (
        <InfoSection>
          <InfoHeader>
            <InfoIcon size={12} />
            System Status
          </InfoHeader>

          <InfoList>
            <li>{`Mode: ${import.meta.env.MODE}`}</li>
            <li>
              {"Chat Mode: "}
              {getCurrentState().mode === CHAT_MODE.INPUT ? "Input" : "Answer"}
            </li>
            <li>
              {"User Query: "}
              {getCurrentState().userQuery
                ? `"${getCurrentState().userQuery.slice(0, 60)}..."`
                : "None"}
            </li>
            <li>
              {"Context URL: "}
              {getCurrentState().contextUrl
                ? `"${getCurrentState().contextUrl?.slice(0, 60)}..."`
                : "None"}
            </li>
          </InfoList>
        </InfoSection>
      )}
    </Container>
  );
};

class DebugDashboardWebComponent extends HTMLElement {
  private root: ReactDOM.Root | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    // Inject base styles including system fonts
    injectBaseStyles(this);

    const showInfo = !this.hasAttribute("hide-info");

    // Create a custom Emotion cache for this shadow root
    const cache = createCache({
      key: "shadow-emotion",
      container: this.shadowRoot as unknown as Element,
    });

    this.root = ReactDOM.createRoot(this.shadowRoot as ShadowRoot);
    this.root.render(
      <CacheProvider value={cache}>
        <DebugDashboardComponent showInfo={showInfo} />
      </CacheProvider>
    );
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

export default DebugDashboardWebComponent;
